import type { ButtonProps, ButtonType } from '@ingka/button';
import Button from '@ingka/button';
import arrowUpToBase from '@ingka/ssr-icon/paths/arrow-up-to-base';
import type { ChangeEvent } from 'react';
import { useCallback, useRef } from 'react';

export type FileUploadButtonProps = {
  type?: ButtonType;
  label: string;
  /**
   * Hint for browsers to guide users towards selecting the correct file type
   */
  accept?: HTMLInputElement['accept'];
  /**
   * If provided, will be called with the file from the file upload
   */
  onUploadFile?: (file: File) => void;
  /**
   * If provided, will be called with the string after parsing the file upload
   */
  onUploadString?: (content: string) => void;
  /**
   * Support some skapa button props
   */
  small?: ButtonProps['small'];
  className?: ButtonProps['className'];
};

export const FileUploadButton: React.FC<FileUploadButtonProps> = ({
  type,
  label,
  accept,
  onUploadFile,
  onUploadString,
  ...skapaButtonProps
}) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const handleOnClick = useCallback(() => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  }, [hiddenFileInput]);

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.item(0);
      if (file) {
        if (onUploadFile) {
          // Return file as is
          onUploadFile(file);
          // Reset input value to be able to upload the same file again
          event.target.value = '';
        }
        if (onUploadString) {
          // Parse the file into a string
          const reader = new FileReader();
          reader.addEventListener(
            'load',
            () => {
              if (typeof reader.result === 'string') {
                onUploadString(reader.result);
              }
            },
            false
          );
          reader.readAsText(file);
        }
      }
    },
    [onUploadFile, onUploadString]
  );

  return (
    <>
      <Button
        type={type}
        iconPosition="leading"
        ssrIcon={arrowUpToBase}
        onClick={handleOnClick}
        {...skapaButtonProps}
      >
        {label}
      </Button>
      <input
        type="file"
        accept={accept}
        ref={hiddenFileInput}
        onChange={handleOnChange}
        style={{ display: 'none' }}
        data-testid="upload-file-input"
      />
    </>
  );
};
